<template> 
  <a-layout class="dashboard-layout">
    <!-- Mobile Header with Hamburger Menu -->
    <div class="mobile-header">
      <button class="hamburger-btn" @click="toggleSidebar">
        <span :class="{ active: !collapsed }"></span>
        <span :class="{ active: !collapsed }"></span>
        <span :class="{ active: !collapsed }"></span>
      </button>
      
      <div class="header-logo">
        <img src="/icons/logo-mechtrace.png" alt="MechTrace">
        <span>MechTrace</span>
      </div>
      
      <div class="header-actions">
        <a-badge :count="5">
          <button class="action-btn">
            <img src="/images/notification.png" alt="Notifications" width="20" height="20">
          </button>
        </a-badge>
      </div>
    </div>

    <!-- Overlay for mobile/tablet -->
    <div 
      v-if="!collapsed && isMobileView" 
      class="sidebar-overlay"
      @click="collapsed = true"
    ></div>

    <!-- Sidebar -->
    <DashboardSidebar 
      :sidebar-collapsed="collapsed" 
      :is-mobile-view="isMobileView"
      sidebar-color="primary" 
      sidebar-theme="light" 
      @toggleSidebar="handleToggleSidebar" 
    /> 

    <!-- Main Content -->
    <a-layout>
      <a-layout-content :class="['dashboard-content', { 'mobile-content': isMobileView }]">
        <router-view />
      </a-layout-content> 
    </a-layout>
  </a-layout> 
</template>

<script setup> 
import { ref, onMounted, onUnmounted } from 'vue'; 
import DashboardSidebar from '@/components/Sidebars/DashboardSidebar.vue'; 

const collapsed = ref(true);
const isMobileView = ref(false);

// Check screen size
const checkScreenSize = () => {
  const width = window.innerWidth;
  isMobileView.value = width < 992; // Bootstrap lg breakpoint
  collapsed.value = isMobileView.value; // hidden on mobile, open on desktop
};

// Toggle sidebar
const toggleSidebar = () => {
  collapsed.value = !collapsed.value;
};

// Handle sidebar toggle from child component
const handleToggleSidebar = (value) => {
  collapsed.value = value;
};

// Lifecycle
onMounted(() => {
  checkScreenSize();
  window.addEventListener('resize', checkScreenSize);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkScreenSize);
});
</script>

<style lang="scss" scoped>
.dashboard-layout {
  min-height: 100vh;
  position: relative;
}

// Mobile Header
.mobile-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 64px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  z-index: 1003;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  .hamburger-btn {
    width: 40px;
    height: 40px;
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 8px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 5px;
    border-radius: 8px;
    transition: background 0.2s;

    &:hover {
      background: #f3f4f6;
    }

    &:active {
      transform: scale(0.95);
    }

    span {
      display: block;
      width: 24px;
      height: 2px;
      background: #111827;
      border-radius: 2px;
      transition: all 0.3s ease;

      &.active {
        &:nth-child(1) {
          transform: translateY(7px) rotate(45deg);
        }
        &:nth-child(2) {
          opacity: 0;
        }
        &:nth-child(3) {
          transform: translateY(-7px) rotate(-45deg);
        }
      }
    }
  }

  .header-logo {
    display: flex;
    align-items: center;
    gap: 10px;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);

    img {
      height: 28px;
      width: auto;
    }

    span {
      font-size: 18px;
      font-weight: 600;
      color: #111827;
    }
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 12px;

    .action-btn {
      width: 40px;
      height: 40px;
      border: none;
      background: transparent;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
      transition: background 0.2s;

      &:hover {
        background: #f3f4f6;
      }
    }
  }
}

// Overlay
.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1001;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

// Main Content
.dashboard-content {
  min-height: 100vh;
  background-color: #f0f2f5;
  transition: margin-left 0.3s ease, padding 0.3s ease;

  // Desktop: with sidebar
  &:not(.mobile-content) {
    margin-left: 250px; // Sidebar width when expanded
  }

  // Mobile/Tablet: no sidebar offset
  &.mobile-content {
    margin-left: 0;
    padding-top: 88px; // Mobile header height + spacing
  }
}

// Responsive breakpoints
@media (max-width: 1400px) {
  .dashboard-content:not(.mobile-content) {
    padding: 20px;
  }
}

@media (max-width: 1200px) {
  .dashboard-content:not(.mobile-content) {
    padding: 18px;
  }
}

// Tablet and below - show mobile header
@media (max-width: 991px) {
  .dashboard-content {
    margin-left: 0 !important;
    padding: 16px;
    padding-top: 80px;
  }

  .mobile-header {
    display: flex;
  }
}

@media (max-width: 768px) {
  .mobile-header {
    padding: 0 16px;
    height: 60px;

    .header-logo {
      span {
        font-size: 16px;
      }
      img {
        height: 24px;
      }
    }
  }

  .dashboard-content {
    padding: 12px;
    padding-top: 72px;
  }
}

@media (max-width: 576px) {
  .mobile-header {
    padding: 0 12px;

    .header-logo span {
      display: none; // Hide text on very small screens
    }
  }

  .dashboard-content {
    padding: 10px;
  }
}

// Hide mobile header on desktop
@media (min-width: 992px) {
  .mobile-header {
    display: none;
  }
}
</style>